<template>
  <div
    ref="panelRef"
    class="grid grid-cols-[60px_1px_1fr] bg-white border border-gray-200/25 tablet-h:overflow-hidden tablet-h:rounded-xl"
  >
    <!-- Time ruler -->
    <div class="h-[22px] sticky left-0 z-10 bg-white" />
    <div class="h-[22px]" />
    <div class="h-[22px] flex items-center pr-[60px]">
      <div class="w-full flex items-center justify-between">
        <template v-for="(tick, i) in ticks" :key="i">
          <span
            v-if="tick.label"
            class="font-secondary text-[12px] leading-[16px] text-gray-300 shrink-0 tabular-nums"
          >
            {{ tick.label }}
          </span>
          <span v-else class="size-1 rounded-full bg-gray-200 shrink-0" />
        </template>
      </div>
    </div>

    <div class="col-span-3 h-px bg-gray-200/25 hidden tablet-h:block" />

    <!-- Transcript row -->
    <template v-if="showTranscript">
      <div
        class="flex items-center justify-center h-[68px] sticky left-0 z-10 bg-white"
      >
        <Icon name="Text" :size="20" class="stroke-[#2d2d2d]" />
      </div>
      <div />
      <div
        ref="transcriptRef"
        class="relative flex items-center h-[68px] overflow-hidden"
      >
        <div class="flex gap-x-1 w-full">
          <div
            v-for="(text, n) in visibleTranscript"
            :key="n"
            class="shrink-0 w-[83px] h-[56px] bg-gray-200/25 rounded pt-[7px] pl-[7px] pr-[3px]"
          >
            <div
              class="font-secondary text-[8px] leading-[10px] text-gray-500 h-[48px] overflow-hidden"
            >
              {{ text }}
            </div>
          </div>
        </div>
        <MatchHighlights :matches="transcriptMatches" :duration="duration" />
      </div>

      <div class="col-span-3 h-px bg-gray-200/25" />
    </template>

    <!-- Audio row -->
    <div
      class="flex items-center justify-center h-[68px] sticky left-0 z-10 bg-white"
    >
      <Icon name="Audio" :size="20" class="stroke-[#2d2d2d]" />
    </div>
    <div />
    <div class="relative flex items-center h-[68px]">
      <div class="w-full h-[42px]">
        <Wave v-if="audioUrl" :url="audioUrl" type="original" />
      </div>
      <MatchHighlights :matches="audioMatches" :duration="duration" />
    </div>

    <!-- Video row -->
    <template v-if="showVideo">
      <div class="col-span-3 h-px bg-gray-200/25" />

      <div
        class="flex items-center justify-center h-[68px] sticky left-0 z-10 bg-white"
      >
        <Icon name="Eye" :size="20" class="stroke-[#2d2d2d]" />
      </div>
      <div />
      <div
        ref="framesRef"
        class="relative flex items-center h-[68px] overflow-hidden"
      >
        <div class="flex w-full gap-x-px h-[56px] overflow-hidden">
          <img
            v-if="!framesReady && frames.length"
            :src="frames[0]"
            class="h-[56px] invisible absolute"
            @load="onFrameLoad"
          />
          <template v-for="(frameUrl, index) in distributedFrames" :key="index">
            <div
              class="relative h-[56px] shrink-0"
              :style="{ width: frameWidth + 'px' }"
            >
              <Skeleton class="absolute inset-0" />
              <img
                :src="frameUrl"
                class="absolute inset-0 h-full w-full opacity-0 transition-opacity duration-300"
                @load="
                  ($event.target as HTMLImageElement).classList.replace(
                    'opacity-0',
                    'opacity-100'
                  )
                "
              />
            </div>
          </template>
          <template v-if="!framesReady">
            <Skeleton
              v-for="n in 9"
              :key="'sk-' + n"
              class="h-[56px] w-16 shrink-0"
            />
          </template>
        </div>
        <MatchHighlights :matches="videoMatches" :duration="duration" />
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import Wave from '@/features/copyright/components/Wave.vue'
import { Icon, Skeleton } from '@dacr/ui'
import { computed, onMounted, onUnmounted, ref, nextTick, watch } from 'vue'
import MatchHighlights from './MatchHighlights.vue'
import type { TimelineMatch } from './timeline-types'

const props = defineProps<{
  duration: number
  audioUrl?: string | null
  transcript?: readonly string[]
  frames?: readonly string[]
  matches?: readonly TimelineMatch[]
  side: 'registered' | 'infringing'
}>()

const panelRef = ref<HTMLElement>()
const transcriptRef = ref<HTMLElement>()
const framesRef = ref<HTMLElement>()

const panelWidth = ref(0)
const transcriptWidth = ref(0)
const framesWidth = ref(0)
const frameWidth = ref(0)

const CARD_WIDTH = 82
const CARD_GAP = 4
const FRAME_HEIGHT = 56
const FRAME_GAP = 1

const transcript = computed(() => props.transcript ?? [])
const frames = computed(() => props.frames ?? [])
const matches = computed(() => props.matches ?? [])

const showTranscript = computed(() => transcript.value.length > 0)
const showVideo = computed(() => frames.value.length > 0)

const sideMatches = (type: 'Audio' | 'Video' | 'Transcript' | 'Text' | 'Image') =>
  matches.value
    .filter((m) => m.type === type)
    .map((m) => ({
      id: m.id,
      start: m[props.side].start,
      end: m[props.side].end,
      level: m.level,
      match: m,
    }))

const audioMatches = computed(() => sideMatches('Audio'))
const videoMatches = computed(() => sideMatches('Video'))
const transcriptMatches = computed(() => [
  ...sideMatches('Transcript'),
  ...sideMatches('Text'),
])

const visibleCardCount = computed(() => {
  const available = transcriptWidth.value - 24
  if (available <= 0) return 0
  const max = Math.floor((available + CARD_GAP) / (CARD_WIDTH + CARD_GAP))
  return Math.max(1, Math.min(transcript.value.length, max))
})

const visibleTranscript = computed(() =>
  transcript.value.slice(0, visibleCardCount.value)
)

const framesReady = computed(() => framesWidth.value > 0 && frameWidth.value > 0)

const onFrameLoad = (event: Event) => {
  const img = event.target as HTMLImageElement
  if (img.naturalWidth > 0 && img.naturalHeight > 0) {
    frameWidth.value = Math.round(
      (FRAME_HEIGHT / img.naturalHeight) * img.naturalWidth
    )
  }
}

const distributedFrames = computed(() => {
  if (!framesReady.value) return []
  const all = frames.value
  if (all.length <= 2) return [...all]
  const maxFrames = Math.max(
    2,
    Math.ceil(
      (framesWidth.value - frameWidth.value) / (frameWidth.value + FRAME_GAP) + 1
    )
  )
  if (all.length <= maxFrames) return [...all]

  const result: string[] = [all[0]]
  const inner = maxFrames - 2
  for (let i = 1; i <= inner; i++) {
    const idx = Math.round((i * (all.length - 1)) / (maxFrames - 1))
    result.push(all[idx])
  }
  result.push(all[all.length - 1])
  return result
})

const formatTime = (seconds: number) => {
  const s = Math.max(0, Math.round(seconds))
  const mm = String(Math.floor(s / 60)).padStart(2, '0')
  const ss = String(s % 60).padStart(2, '0')
  return `${mm}:${ss}`
}

const ticks = computed(() => {
  const contentWidth = panelWidth.value - 120
  if (contentWidth <= 0 || !props.duration) return []

  const labelCount = Math.max(2, Math.min(12, Math.floor(contentWidth / 110)))
  const dotsBetween = 3
  const out: Array<{ label: string | null }> = []
  for (let i = 0; i < labelCount; i++) {
    const t = (i / (labelCount - 1)) * props.duration
    out.push({ label: formatTime(t) })
    if (i < labelCount - 1) {
      for (let d = 0; d < dotsBetween; d++) out.push({ label: null })
    }
  }
  return out
})

const observe = (el: HTMLElement | undefined, setter: (w: number) => void) => {
  if (!el) return null
  const ro = new ResizeObserver(([entry]) => {
    setter(entry.contentRect.width)
  })
  ro.observe(el)
  return ro
}

let observers: Array<ResizeObserver | null> = []

onMounted(async () => {
  await nextTick()
  if (panelRef.value)
    panelWidth.value = panelRef.value.getBoundingClientRect().width
  if (transcriptRef.value)
    transcriptWidth.value = transcriptRef.value.getBoundingClientRect().width
  if (framesRef.value)
    framesWidth.value = framesRef.value.getBoundingClientRect().width

  observers = [
    observe(panelRef.value, (w) => (panelWidth.value = w)),
    observe(transcriptRef.value, (w) => (transcriptWidth.value = w)),
    observe(framesRef.value, (w) => (framesWidth.value = w)),
  ]
})

onUnmounted(() => {
  observers.forEach((o) => o?.disconnect())
})

watch(
  () => frames.value[0],
  () => {
    frameWidth.value = 0
  }
)
</script>
